import { ChevronLeft } from 'lucide-react'
import type { ThreatMapData } from '@/types/map'
import { clockUTC, dateUTC, incidentStatusVar, severityVar } from './map-display-utils'
import { ProvenanceDetails, ProvenanceMiniLine, ReasonChips } from './ProjectionProvenance'
import './map-console.css'

export interface SiteViewProps {
  data: ThreatMapData
  targetId: string
  onBack: () => void
}

export function SiteView({ data, targetId, onBack }: SiteViewProps) {
  const target = data.targets.find((t) => t.id === targetId)
  if (!target) {
    return (
      <div className="flex h-full flex-col items-start gap-3 p-8 text-[color:var(--text-tertiary)]">
        <BackButton onBack={onBack} />
        <p>Target not found.</p>
      </div>
    )
  }

  const incidents = data.incidents
    .filter((i) => i.targetId === targetId)
    .sort((a, b) => b.startedAt - a.startedAt)

  return (
    <div className="flex h-full flex-col gap-3 p-4">
      <header className="flex flex-wrap items-center gap-3">
        <BackButton onBack={onBack} />
        <div className="min-w-0">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-tertiary)]">
            Site Focus
          </span>
          <h2 className="truncate font-heading text-lg leading-tight text-[color:var(--text-primary)]">
            {target.name}
          </h2>
        </div>
        <span className="ml-auto flex items-center gap-2">
          <span
            className="rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em]"
            style={{
              borderColor: 'var(--severity-high)',
              color: 'var(--severity-high)',
            }}
          >
            {incidents.length} vectors
          </span>
        </span>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-[300px_1fr] gap-4 max-[1100px]:grid-cols-1">
        <aside className="soc-panel min-h-0 overflow-y-auto p-4">
          <h3 className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.18em] text-[color:var(--text-secondary)]">
            <span className="text-[color:var(--interactive-primary)]" aria-hidden>
              ▸
            </span>
            Site Detail
          </h3>
          <dl className="mt-3 divide-y divide-[color:var(--border-subtle)] border-y border-[color:var(--border-subtle)] text-sm">
            <Field label="Sector">
              <span className="capitalize">{target.sector}</span>
            </Field>
            <Field label="Country">{target.countryIso2}</Field>
            <Field label="Known attacks">{target.incidentCount}</Field>
            <Field label="Active vectors">{incidents.length}</Field>
            {target.reasonLabel && <Field label="Geo reason">{target.reasonLabel}</Field>}
            <Field label="Coordinates">
              {target.latitude.toFixed(2)}, {target.longitude.toFixed(2)}
            </Field>
          </dl>
          {(target.unknownReasons?.length ||
            target.provenance?.length ||
            target.availableButNotSelected?.length) && (
            <div className="mt-3 space-y-2">
              <ReasonChips reasons={target.unknownReasons} label="Target projection reasons" />
              <ProvenanceDetails
                provenance={target.provenance}
                availableButNotSelected={target.availableButNotSelected}
                evidenceIds={[{ label: 'Target', value: target.id }]}
              />
            </div>
          )}
        </aside>

        <section className="soc-panel flex min-h-0 flex-col overflow-hidden">
          <header className="soc-panel-head flex shrink-0 items-center justify-between px-3 py-2">
            <h3 className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.18em] text-[color:var(--text-secondary)]">
              <span className="text-[color:var(--interactive-primary)]" aria-hidden>
                ▸
              </span>
              Active Vectors
            </h3>
            <span className="font-mono text-[10px] tracking-widest text-[color:var(--text-tertiary)]">
              N={incidents.length}
            </span>
          </header>
          {incidents.length === 0 ? (
            <p className="p-4 text-sm text-[color:var(--text-tertiary)]">
              No recorded incidents for this target in the active window.
            </p>
          ) : (
            <ul className="min-h-0 flex-1 overflow-y-auto">
              {incidents.map((inc) => (
                <li
                  key={inc.id}
                  data-testid="site-incident-row"
                  className="flex flex-col gap-0.5 border-b border-[color:var(--border-subtle)] px-3 py-2 text-sm last:border-0"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ background: severityVar(inc.severity) }}
                      aria-hidden="true"
                    />
                    <span
                      className="font-mono text-[10px] uppercase tracking-[0.12em]"
                      style={{ color: severityVar(inc.severity) }}
                    >
                      {inc.severity}
                    </span>
                    <span className="ml-auto font-mono text-[10px] text-[color:var(--text-tertiary)]">
                      {dateUTC(inc.startedAt)} {clockUTC(inc.startedAt)}Z
                    </span>
                  </div>
                  <span className="truncate text-[color:var(--text-primary)]">
                    {inc.narrative || inc.id}
                  </span>
                  <span
                    className="font-mono text-[9px] uppercase tracking-[0.12em]"
                    style={{ color: incidentStatusVar(inc.status) }}
                  >
                    {inc.status}
                  </span>
                  <ReasonChips reasons={inc.unknownReasons} max={2} label={`Reasons for ${inc.id}`} />
                  {inc.provenance && inc.provenance.length > 0 && (
                    <span className="truncate font-mono text-[9px] uppercase tracking-[0.12em] text-[color:var(--text-tertiary)]">
                      <ProvenanceMiniLine provenance={inc.provenance} />
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}

function BackButton({ onBack }: { onBack: () => void }) {
  return (
    <button
      onClick={onBack}
      aria-label="Back"
      className="inline-flex shrink-0 items-center gap-1 self-start rounded-md border border-[color:var(--border-subtle)] px-2 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-secondary)] transition-colors hover:border-[color:var(--interactive-primary)] hover:text-[color:var(--interactive-primary)]"
    >
      <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
      Back
    </button>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3 py-1.5">
      <dt className="w-28 shrink-0 font-mono text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-tertiary)]">
        {label}
      </dt>
      <dd className="min-w-0 flex-1 font-mono text-xs text-[color:var(--text-primary)]">
        {children}
      </dd>
    </div>
  )
}
